/**
 * AutoSaveIndicator — CR-6165
 *
 * Status pill for forms that auto-save in the background, telling the user
 * their work is safe without them having to click anything.
 *
 * Deliberately separate from RFQ's `WizardSaveStatus`: that pill says
 * "Progress saved — RFQ not created yet", wording that only makes sense for a
 * wizard that builds a record it has not created yet. A questionnaire answer
 * IS the record, so the copy here is about the answers, and it shows the
 * clock time the ticket asked for ("Saved 14:32") rather than only a state.
 *
 * @param status        Save state from `useAutoSave`.
 * @param lastSavedAt   Timestamp of the last successful save, auto or manual.
 * @param hasUnsavedChanges Edits made since that save — shown as "Unsaved
 *   changes" so the pill never reads "Saved" while newer work is pending.
 * @param saveButtonLabel The host page's manual save control, named exactly as
 *   it appears on screen ("Save Draft" on qualifications, "Save Progress" on
 *   onboarding). The failure tooltip tells the user to click it, so it has to
 *   match the button they can actually see.
 * @param className     Optional extra classes for layout at the call site.
 */
import { Cloud, CloudOff, Loader2, PencilLine } from 'lucide-react';
import { AutoSaveStatus } from '../../hooks/useAutoSave';

interface AutoSaveIndicatorProps {
  status: AutoSaveStatus;
  lastSavedAt: Date | null;
  hasUnsavedChanges?: boolean;
  saveButtonLabel: string;
  className?: string;
}

export function AutoSaveIndicator({
  status,
  lastSavedAt,
  hasUnsavedChanges = false,
  saveButtonLabel,
  className = '',
}: AutoSaveIndicatorProps) {
  const base = `flex items-center gap-1.5 text-xs ${className}`;

  if (status === 'saving') {
    return (
      <span className={`${base} text-muted-foreground`} aria-live="polite">
        <Loader2 className="h-3 w-3 animate-spin" />
        Saving…
      </span>
    );
  }

  if (status === 'error') {
    return (
      <span
        className={`${base} text-amber-600 dark:text-amber-500`}
        title={`The last auto-save failed and will not retry on its own. Click ${saveButtonLabel} to try again — do not close this page until it succeeds.`}
        aria-live="polite"
      >
        <CloudOff className="h-3 w-3" />
        Not saved
      </span>
    );
  }

  // Edits newer than the last save. Without this the pill would keep asserting
  // "Saved 14:32" through the whole debounce window — exactly the seconds the
  // leave-page guard exists to protect.
  if (hasUnsavedChanges) {
    return (
      <span className={`${base} text-muted-foreground`} aria-live="polite">
        <PencilLine className="h-3 w-3" />
        Unsaved changes
      </span>
    );
  }

  if (status === 'saved' && lastSavedAt) {
    // Locale-aware HH:MM. The full date/time goes in the tooltip so a
    // supplier returning the next day is not misled by a bare clock time.
    const time = lastSavedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return (
      <span
        className={`${base} text-muted-foreground`}
        title={`Your answers were saved at ${lastSavedAt.toLocaleString()}.`}
        aria-live="polite"
      >
        <Cloud className="h-3 w-3" />
        Saved {time}
      </span>
    );
  }

  // 'idle' — nothing saved yet this session. Render nothing rather than an
  // empty pill, so the button row does not jump when the first save lands.
  return null;
}

export default AutoSaveIndicator;
